import { useState, useEffect } from "react";
import { Example } from "./Example";
import { useTranslation } from "react-i18next";
import { Dropdown, IDropdownOption } from "@fluentui/react";

import styles from "./Example.module.css";

interface Props {
    onExampleClicked: (value: string) => void;
    useMultimodalAnswering?: boolean;
    searchMode?: "notes" | "help";
    docCategory?: string;
}

// Category definitions with display labels for Clinical Notes mode
const EXAMPLE_CATEGORIES = [
    { key: "sppReview", label: "SPP Review" },
    { key: "safety", label: "Safety & Crisis" },
    { key: "assessmentTrends", label: "Assessment Trends" },
    { key: "engagement", label: "Engagement & Care Context" },
    { key: "trajectory", label: "Whole Patient Trajectory" },
    { key: "evidence", label: "Evidence Checks" }
];

// Sub-categories for help examples, organized by doc category
const HELP_SUBCATEGORIES: Record<string, { key: string; label: string }[]> = {
    clinical: [
        { key: "clinicalDocs", label: "Clinical Documentation" },
        { key: "billingCodes", label: "Billing Codes" }
    ],
    "poc-app": [
        { key: "avatarNav", label: "Avatar Navigation" },
        { key: "lookupApp", label: "Lookup App" }
    ],
    hr: [{ key: "benefits", label: "Benefits" }]
};

const MULTIMODAL_CATEGORIES = [{ key: "multimodal", label: "Document Analysis" }];

export const ExampleList = ({ onExampleClicked, useMultimodalAnswering, searchMode = "notes", docCategory = "clinical" }: Props) => {
    const { t } = useTranslation();

    const getDefaultCategory = () => {
        if (useMultimodalAnswering) return "multimodal";
        if (searchMode === "help") {
            // Default to first subcategory of the selected doc category
            const subs = HELP_SUBCATEGORIES[docCategory];
            return subs?.[0]?.key || "clinicalDocs";
        }
        return "sppReview";
    };

    const [selectedCategory, setSelectedCategory] = useState<string>(getDefaultCategory());

    // Reset selected category when searchMode or docCategory changes
    useEffect(() => {
        setSelectedCategory(getDefaultCategory());
    }, [searchMode, useMultimodalAnswering, docCategory]);

    const getCategories = () => {
        if (useMultimodalAnswering) return MULTIMODAL_CATEGORIES;
        if (searchMode === "help") {
            return HELP_SUBCATEGORIES[docCategory] || HELP_SUBCATEGORIES["clinical"];
        }
        return EXAMPLE_CATEGORIES;
    };

    const categories = getCategories();

    const dropdownOptions: IDropdownOption[] = categories.map(cat => ({
        key: cat.key,
        text: cat.label
    }));

    // Get examples for selected category
    const getExamples = (): string[] => {
        if (useMultimodalAnswering) {
            return [t("multimodalExamples.1"), t("multimodalExamples.2"), t("multimodalExamples.3")];
        }
        if (searchMode === "help") {
            return [t(`helpExamples.${selectedCategory}.1`), t(`helpExamples.${selectedCategory}.2`), t(`helpExamples.${selectedCategory}.3`)];
        }
        return [t(`examples.${selectedCategory}.1`), t(`examples.${selectedCategory}.2`), t(`examples.${selectedCategory}.3`)];
    };

    const examples = getExamples();

    // Only show subcategory dropdown if there are multiple subcategories
    const showSubcategoryDropdown = !useMultimodalAnswering && categories.length > 1;

    return (
        <div className={styles.examplesContainer}>
            {showSubcategoryDropdown && (
                <Dropdown
                    selectedKey={selectedCategory}
                    options={dropdownOptions}
                    onChange={(_, option) => option && setSelectedCategory(option.key as string)}
                    className={styles.categoryDropdown}
                    styles={{
                        dropdown: { width: 220 },
                        title: { fontSize: 13 }
                    }}
                />
            )}
            <ul className={styles.examplesNavList}>
                {examples.map((question, i) => (
                    <li key={`${selectedCategory}-${i}`}>
                        <Example text={question} value={question} onClick={onExampleClicked} />
                    </li>
                ))}
            </ul>
        </div>
    );
};
